import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, Sailboat, Handshake, User, Pencil, MessageSquare } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useDeal, useUpdateDeal } from '@/features/customers/deal-hooks'
import { DealEditDialog } from '@/features/customers/components/deal-edit-dialog'
import { DealAddOnsCard } from '@/features/customers/components/deal-addons-card'
import { MessagesSidebar } from '@/features/messaging/components/messages-sidebar'
import { DocumentsCard } from '@/features/documents/components/documents-card'
import { RegistrationCard } from '@/features/documents/components/registration-card'
import { formatCurrency } from '@/lib/format'
import { requireFlow } from '@/features/auth/guards'
import { DEAL_STAGES, STAGE_LABELS, STAGE_DOT, type DealStage } from '@/types/deal'

export const Route = createFileRoute('/_authenticated/sales/deals/$dealId')({
  beforeLoad: requireFlow('sales'),
  component: DealDetailPage,
})

function DealDetailPage() {
  const { dealId } = Route.useParams()
  const { data: deal, isLoading, isError } = useDeal(dealId)
  const updateMutation = useUpdateDeal()

  const [editOpen, setEditOpen] = useState(false)
  const [messageOpen, setMessageOpen] = useState(false)

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (isError || !deal) {
    return (
      <div className="flex flex-col items-center gap-4 py-16">
        <p className="text-muted-foreground">Deal not found.</p>
        <Button variant="outline" asChild>
          <Link to="/sales" search={{ tab: 'deals' }}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Deals
          </Link>
        </Button>
      </div>
    )
  }

  // Advance the stage without losing the deal's other fields (the PUT is a
  // full replace — the kanban learned this the hard way). The base value
  // rides along; the add-on rollup is recomputed server-side.
  const handleStageChange = (stage: DealStage) => {
    updateMutation.mutate({
      id: deal.id,
      body: {
        title: deal.title,
        base_value: deal.base_value,
        stage,
        owner: deal.owner,
        customer_id: deal.customer_id,
        boat_id: deal.boat_id ?? undefined,
        expected_close: deal.expected_close,
      },
    })
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Button variant="ghost" size="sm" className="-ml-2 mb-1 text-muted-foreground" asChild>
            <Link to="/sales" search={{ tab: 'deals' }}>
              <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Deals
            </Link>
          </Button>
          <h1 className="flex items-center gap-3 text-2xl font-bold tracking-tight">
            {deal.title}
            <Badge variant="secondary" className="gap-1.5">
              <span className={`h-2 w-2 rounded-full ${STAGE_DOT[deal.stage]}`} />
              {STAGE_LABELS[deal.stage]}
            </Badge>
          </h1>
          <p className="text-muted-foreground">
            {formatCurrency(deal.value)}
            {deal.owner ? ` · Sales Rep: ${deal.owner}` : ''}
            {deal.expected_close
              ? ` · Expected close ${new Date(deal.expected_close).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}`
              : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setMessageOpen(true)}>
            <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> Messages
          </Button>
          <MessagesSidebar
            open={messageOpen}
            onOpenChange={setMessageOpen}
            customerId={deal.customer_id}
            dealId={deal.id}
          />
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
          </Button>
          {/* Delete lives inside the edit dialog, behind a confirmation. */}
          <DealEditDialog open={editOpen} onOpenChange={setEditOpen} mode="edit" deal={deal} />
        </div>
      </div>

      {/* Stage advancement: quick-move to any other stage of the pipeline. */}
      <div className="flex flex-wrap items-center gap-2">
        {DEAL_STAGES.filter((s) => s !== deal.stage).map((stage) => (
          <Button
            key={stage}
            variant="outline"
            size="sm"
            disabled={updateMutation.isPending}
            onClick={() => handleStageChange(stage)}
          >
            Move to {STAGE_LABELS[stage]}
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Handshake className="h-4 w-4" /> Deal Overview
          </CardTitle>
          <CardDescription>The sale this paperwork belongs to.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Customer</div>
            <Link
              to="/sales/$customerId"
              params={{ customerId: deal.customer.id }}
              className="flex items-center gap-1.5 text-sm font-medium hover:underline"
            >
              <User className="h-3.5 w-3.5" />
              {deal.customer.first_name} {deal.customer.last_name}
            </Link>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Boat</div>
            {deal.boat ? (
              <Link
                to="/inventory/$boatId"
                params={{ boatId: deal.boat.id }}
                className="flex items-center gap-1.5 text-sm font-medium hover:underline"
              >
                <Sailboat className="h-3.5 w-3.5" />
                {deal.boat.year} {deal.boat.make} {deal.boat.model}
              </Link>
            ) : (
              <span className="text-sm text-muted-foreground">No boat linked</span>
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Value</div>
            <div className="text-sm font-medium">{formatCurrency(deal.value)}</div>
            {deal.addons_total > 0 && (
              <div className="text-xs text-muted-foreground">
                {formatCurrency(deal.base_value)} base + {formatCurrency(deal.addons_total)} add-ons
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <DealAddOnsCard dealId={dealId} boatType={deal.boat?.type} baseValue={deal.base_value} />

      <DocumentsCard dealId={dealId} />
      <RegistrationCard dealId={dealId} />
    </div>
  )
}
